import { z } from "zod";
import { request } from "./client";

/**
 * POST /me/consents, with the profile and the date of birth riding along.
 *
 * Same endpoint as `recordConsents` — deliberately. Name, date of birth, country
 * and the consent are ONE act taken on ONE page, and the server writes them as
 * one append-only row; splitting the call would create a window where a consent
 * row exists without the details it was given with, which is the thing the row
 * is evidence of.
 *
 * ## Why the date of birth is a date and not a tick
 *
 * The old gate asked "are you 18 or over?" and believed the answer. There was no
 * false path, because a self-declaration has nothing to verify. A real date does,
 * and the arithmetic is the SERVER's: a client that computes its own eligibility
 * is a client that can claim it. So this sends the raw date and reads the verdict
 * off the status code.
 *
 * A refusal comes back as `403 under_18`, and the server writes NOTHING when it
 * refuses — no row, no attribute, no account state. Recording the attempt would
 * mean storing a minor's date of birth in order to remember not to serve them.
 * Callers must therefore branch on the code rather than retrying.
 */

const consentAckSchema = z.object({ recordedAt: z.iso.datetime() });

export interface Onboarding {
  givenName: string;
  familyName: string;
  /** YYYY-MM-DD, exactly as `<input type="date">` produces it. */
  birthdate: string;
  /**
   * ISO-3166 alpha-2. Upper-cased server-side regardless.
   *
   * Australian residence QUALIFIES an account. A non-AU answer comes back
   * `403 country_not_supported` and, like the under-18 refusal, writes nothing
   * at all. This is a different fact from the AustraliaOnly WAF rule, which
   * gates on where the request comes from rather than where the person lives.
   */
  country: string;
  termsVersion: string;
}

export async function recordOnboarding(o: Onboarding): Promise<void> {
  await request("/me/consents", consentAckSchema, {
    method: "POST",
    body: {
      acceptedTerms: true,
      /* Bundled with the terms by the notice on the page — see ConsentNotice.
         Sent as a value rather than assumed server-side so a preference centre
         can one day write false to it without the record shape changing. */
      marketingOptIn: true,
      termsVersion: o.termsVersion,
      givenName: o.givenName,
      familyName: o.familyName,
      birthdate: o.birthdate,
      country: o.country,
    },
  });
}

/**
 * Re-acceptance, for an account that has done all of this once already.
 *
 * ═══ WHY THIS IS NOT `recordOnboarding` ═══
 *
 * A `TERMS_VERSION` bump sends every existing account back through the consent
 * guard. Before this existed they landed on /welcome — headed "Finish setting
 * up", promising "this is the only time you'll be asked", with four empty
 * required fields. A returning customer had to retype a date of birth the API
 * deliberately never gives back (`profileSchema` has no `birthdate`), the server
 * did the age arithmetic on whatever they typed, and a mistyped year answered
 * 403 under_18 and locked a paying account out of a product it had bought.
 *
 * So this sends NO BIRTHDATE, and the age is therefore not re-checked. That is
 * the intended behaviour, not an omission: `dobVerified` is already true on the
 * profile, it was set by the server from a date it verified itself, and asking
 * again proves nothing that is not already recorded while creating the lockout
 * above. Ned's call, 2026-08-26.
 *
 * ═══ THE PROFILE IS RESENT, NOT LEFT TO A SERVER-SIDE DEFAULT ═══
 *
 * `givenName`, `familyName` and `country` come straight back from /me and go
 * straight out again unchanged. The handler does fall back to the stored value
 * for a field it is not given — but relying on that would make this call's
 * correctness depend on behaviour no test here can see, and the values are in
 * hand anyway. A field /me did not return is omitted rather than sent as null.
 *
 * ═══ ⚠️ ONE SERVER ASSUMPTION, AND IT IS NOT VERIFIED HERE ═══
 *
 * This depends on `POST /me/consents` accepting a body with NO `birthdate`. The
 * MSW handler does — it guards the age arithmetic behind
 * `if (body.birthdate !== undefined)` and preserves `dobVerified` — and that
 * handler was written against the real one. But the mock is this repo's MODEL of
 * the backend, and the waitlist shipped broken for weeks because a mock modelled
 * a backend somebody intended rather than the one deployed.
 *
 * **Checked 2026-08-26, and the answer was both halves at once.** With a real ID
 * token, `POST /me/consents` carrying no birthdate answered **200** — so the
 * shape is accepted, and the mock was right about that.
 *
 * 🛑 **But the write does not take effect.** `GET /me` immediately afterwards
 * still reported `termsVersion: "2026-07-17"` and a consent row three days old,
 * while the 200 carried a freshly generated `recordedAt`. First consent
 * persists — nobody could sign up otherwise — the UPDATE does not. Until that is
 * fixed server-side, this call reports success and changes nothing, which is why
 * TermsUpdated re-reads /me before believing it. See LAUNCH.md.
 *
 * The lesson is narrower than "the mock lied": the mock was accurate about the
 * REQUEST and silent about the EFFECT, and a 200 only ever proves the first.
 */
export interface Reconsent {
  termsVersion: string;
  givenName?: string | null;
  familyName?: string | null;
  country?: string | null;
}

export async function recordReconsent(r: Reconsent): Promise<void> {
  await request("/me/consents", consentAckSchema, {
    method: "POST",
    body: {
      acceptedTerms: true,
      /* Asserted `true` because the page asserting it is on screen: TermsUpdated
         renders the same ConsentNotice /welcome and /signup do, so the record
         matches what the customer was shown.

         ⚠️ THIS BECOMES WRONG THE DAY A PREFERENCE CENTRE EXISTS. /me does not
         return `marketingOptIn`, so this cannot preserve a choice it cannot
         read, and re-asserting true would silently re-opt-in anyone who had
         opted out. Whoever builds that centre must add the field to
         `consentSchema` and make this send what it reads. */
      marketingOptIn: true,
      termsVersion: r.termsVersion,
      /* Omitted, never nulled, when /me did not carry them — an older account
         can legitimately have no name on file, and sending null would overwrite
         a value the server may hold and we do not. */
      ...(r.givenName ? { givenName: r.givenName } : {}),
      ...(r.familyName ? { familyName: r.familyName } : {}),
      ...(r.country ? { country: r.country } : {}),
    },
  });
}
